// src/screens/ResultsScreen/ResultsScreen.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { callApi, fetchWithRetry } from '../../utils/api';

import './results-screen.css';

// Updated formatTitle function
export const formatTitle = (position, betType, config = {}) => {
  if (!position || !betType) {
    return 'Range';
  }

  if (betType.includes('SB_LIMP') || betType === 'LIMP' ||
      betType.includes('LIMP') || betType === 'SB_LIMP') {
    return `${position} Limp Range`;
  }

  if (betType === 'FACING_OPEN_CALL' || betType.includes('FACING_OPEN_CALL')) {
    return config.openPosition
      ? `${position} Call vs ${config.openPosition} Open Range`
      : `${position} Call vs Open Range`;
  }

  if (betType === 'OPEN') {
    return `${position} Open Range`;
  }

  if (betType === 'FACING_3BET_CALL' || betType.includes('FACING_3BET_CALL')) {
    return config.threeBetPosition
      ? `${position} Call vs ${config.threeBetPosition} 3Bet Range`
      : `${position} Call vs 3Bet Range`;
  }

  if (betType === 'FACING_3BET_4BET' || betType.includes('FACING_3BET_4BET')) {
    return config.threeBetPosition
      ? `${position} 4Bet vs ${config.threeBetPosition} 3Bet Range`
      : `${position} 4Bet vs 3Bet Range`;
  }

  if (betType === 'FACING_OPEN_3BET' || betType.includes('FACING_OPEN_3BET')) {
    return config.openPosition
      ? `${position} 3Bet vs ${config.openPosition} Open Range`
      : `${position} 3Bet Range`;
  }

  const action = betType.toLowerCase()
    .replace('facing_', '')
    .replace('_', ' ')
    .replace('3bet', '3Bet')
    .replace('4bet', '4Bet');

  return `${position} ${action} Range`;
};

const ENCOURAGEMENT = {
  low: [
    "Not your final form yet. Run it back.",
    "Rough one. Now you know what to fix.",
    "Missed a few. That's where the edge starts.",
    "Learning spot. Queue it up again.",
  ],
  developing: [
    "Getting there. Just needs tightening.",
    "You see it. Now clean it up.",
    "Solid base. Keep building from here.",
    "Closer than it feels. Stay on it.",
  ],
  strong: [
    "Now we're talking. This is strong play.",
    "Sharp overall. Just refine the edges.",
    "Winning range. Dial in the details.",
    "You're ahead. Now make it consistent.",
  ],
  elite: [
    "Clean. That's high-level play.",
    "Crusher territory. Keep it sharp.",
    "You're not guessing—you know.",
    "Elite. Now make it automatic.",
  ],
};

const getEncouragement = (score) => {
  let pool;
  if (score < 50) pool = ENCOURAGEMENT.low;
  else if (score < 70) pool = ENCOURAGEMENT.developing;
  else if (score < 90) pool = ENCOURAGEMENT.strong;
  else pool = ENCOURAGEMENT.elite;
  return pool[Math.floor(Math.random() * pool.length)];
};

const ResultsScreen = () => {
  console.log('ResultsScreen mounted');
  const [savingStatus, setSavingStatus] = useState('pending');
  const encouragementRef = useRef(null);
  // Holds the in-flight /api/submit-quiz promise so the nav handlers below
  // can await it before leaving this screen — see savePromiseRef.current
  // usage in handleTryAgain/handleNewQuiz/handleDashboard.
  const savePromiseRef = useRef(null);

  let gameData;
  try {
    gameData = useGame();
  } catch (error) {
    console.error('Failed to load game context:', error);
    return <div>Error: Game context not available</div>;
  }

  const { gameState, startNewGame, quizAnalytics } = gameData;
  const navigate = useNavigate();
  const { user } = useAuth();

  const { correctAnswers, questionCount, gameConfig } = gameState;
  const percentage = Math.round((correctAnswers / questionCount) * 100);

  // Pick encouragement once on mount
  if (!encouragementRef.current) {
    encouragementRef.current = getEncouragement(percentage);
  }

  // Game type label
  const getGameTypeLabel = () => {
    return 'Cash Game';
  };

  // Quiz subtitle: range type
  const rangeTitle = formatTitle(
    gameConfig.heroPosition,
    gameConfig.betType,
    {
      openPosition: gameConfig.openPosition,
      threeBetPosition: gameConfig.threeBetPosition
    }
  );
  const quizTypeString = rangeTitle;

  useEffect(() => {
    const saveQuizResults = async () => {
      try {
        setSavingStatus('pending');

        // ---- Build the rich payload from the last finalized quiz ----
        const results     = quizAnalytics.results || [];
        const lastQuiz    = results[results.length - 1];
        const now         = new Date().toISOString();

        // Fallback: if no finalized quiz found (edge case), use gameState only
        const sessionId   = lastQuiz?.id || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const startedAt   = lastQuiz?.startTime  || now;
        const completedAt = lastQuiz?.endTime    || now;
        const questions   = lastQuiz?.questions  || [];

        const sessionPayload = {
          session_id:         sessionId,
          user_id:            user?.uid,
          started_at:         startedAt,
          completed_at:       completedAt,
          status:             'completed',

          quiz_type:          gameConfig.quizType || (gameConfig.format ? 'BLAST' : 'CASH'),
          game_format:        gameConfig.format   || null,
          stack_depth:        gameConfig.stack    || null,
          hero_position:      gameConfig.heroPosition || gameConfig.position || null,
          villain_position:   gameConfig.villainPos   || null,

          scenario_node:      questions[0]?.scenario_node || null,
          action_type:        questions[0]?.action_type   || null,
          training_filter:    gameConfig.filterMode       || null,

          bet_type:           gameConfig.betType          || null,
          open_position:      gameConfig.openPosition     || null,
          three_bet_position: gameConfig.threeBetPosition || null,

          total_questions:    questionCount,
          total_correct:      correctAnswers,
          score_percentage:   percentage,
        };

        const questionsPayload = questions.map((q, idx) => ({
          answered_at:        q.answered_at     || q.timestamp || now,
          question_number:    q.questionNumber  || q.question_number || (idx + 1),

          quiz_type:          q.quiz_type       || sessionPayload.quiz_type,
          game_format:        q.game_format     || sessionPayload.game_format,
          stack_depth:        q.stack_depth     || sessionPayload.stack_depth,
          hero_position:      q.hero_position   || sessionPayload.hero_position,
          villain_position:   q.villain_position || sessionPayload.villain_position,
          scenario_node:      q.scenario_node   || sessionPayload.scenario_node,
          action_type:        q.action_type     || null,
          training_filter:    q.training_filter || sessionPayload.training_filter,
          bet_type:           q.bet_type        || sessionPayload.bet_type,

          hand_combo:         q.hand_combo      || '',
          hand_class:         q.hand_class      || '',
          suitedness:         q.suitedness      || 'offsuit',
          hand_category:      q.hand_category   || null,
          rank_cluster:       q.rank_cluster    || null,

          correct_action:     q.correct_action     || 'Fold',
          correct_frequency:  q.correct_frequency  ?? null,
          correct_raise_size: q.correct_raise_size || null,

          selected_action:    q.selected_action    || 'Fold',
          selected_frequency: q.selected_frequency ?? null,
          selected_raise_size: q.selected_raise_size || null,

          is_correct:         q.isCorrect ?? q.is_correct ?? false,
          response_time_ms:   q.response_time_ms || null,
          error_type:         q.error_type       || null,
        }));

        if (window.usingLocalStorageOnly) {
          // Persist locally only (no DB). Namespaced by uid so this fallback
          // never mixes one account's results into another's on the same device.
          const storageKey = user?.uid ? `quiz_results_${user.uid}` : 'quiz_results';
          const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
          existing.push({
            id: sessionId, startTime: startedAt, endTime: completedAt,
            config: gameConfig, totalCorrect: correctAnswers, accuracy: percentage,
            questions: questionsPayload
          });
          localStorage.setItem(storageKey, JSON.stringify(existing));
          setSavingStatus('success');
          return;
        }

        const payload = { session: sessionPayload, questions: questionsPayload };
        console.log('[submit-quiz] sending payload:', JSON.stringify(payload, null, 2));

        const response = await fetchWithRetry('/api/submit-quiz', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
          body: JSON.stringify(payload)
        });

        const responseBody = await response.json().catch(() => ({}));
        console.log('[submit-quiz] response status:', response.status, responseBody);

        if (response.ok) {
          setSavingStatus('success');
        } else {
          console.error('[submit-quiz] FAILED:', responseBody);
          setSavingStatus('error');
        }
      } catch (error) {
        console.error('[submit-quiz] exception:', error);
        setSavingStatus('error');
      }
    };

    savePromiseRef.current = saveQuizResults();
  }, []);

  // All three nav handlers await the /api/submit-quiz save before leaving
  // this screen. Without this, a fast tap (very plausible right after
  // seeing results) can navigate to /dashboard before the POST's DB commit
  // finishes, so the dashboard's next GET /api/user-stats reads a stale
  // total_quizzes and the empty state incorrectly stays up. The server
  // commits before responding (see server.js /api/submit-quiz), so
  // awaiting the resolved fetch is sufficient — no server change needed.
  const handleTryAgain = async () => {
    await savePromiseRef.current;
    await startNewGame({
      ...gameConfig,
      resetType: 'SAME_CONFIG'
    });
    navigate('/preflop');
  };

  const handleNewQuiz = async () => {
    await savePromiseRef.current;
    await startNewGame({ resetType: 'FULL_RESET' });
    navigate('/quiz-config');
  };

  const handleDashboard = async () => {
    await savePromiseRef.current;
    await startNewGame({ resetType: 'FULL_RESET' });
    navigate('/dashboard');
  };

  return (
    <div className="results-screen">
      <div className="results-content">

        <div className="results-top">
          <div className="results-image-wrap">
            <img
              src="/svgs/results.svg"
              alt="Quiz Results"
              className="results-icon"
            />
          </div>

          <div className="results-header">
            <p className="results-game-type">{getGameTypeLabel()}</p>
            <h1 className="results-title">Quiz Results</h1>
            <p className="results-subtitle">{quizTypeString}</p>
          </div>

          <div className="results-score">
            <div className="results-percentage">{percentage}<span className="results-pct-sign">%</span></div>
            <p className="results-correct-text">{correctAnswers} of {questionCount} Correct</p>
          </div>

        </div>

        <div className="results-buttons">
          <div className="results-buttons-row">
            <button className="results-btn results-btn--green" onClick={handleTryAgain}>
              Try Again
            </button>
            <button className="results-btn results-btn--blue" onClick={handleNewQuiz}>
              New Quiz
            </button>
          </div>
          <button className="results-btn results-btn--orange results-btn--full" onClick={handleDashboard}>
            Dashboard
          </button>
        </div>

      </div>
    </div>
  );
};

export default ResultsScreen;
